import { ChangeDetectionStrategy, Component, OnInit, signal, Signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { Channel, Friendship, Notification, SystemNotification } from '../../dto';
import { AuthService } from '../../services/auth.service';
import { ChatbarService } from '../../services/chatbar.service';
import { InboxService } from '../../services/inbox.service';
import { NotificationService } from '../../services/notification.service';
import { RandomEventService } from '../../services/randomevent.service';
import { SystemNotificationService } from '../../services/system-notification.service';
import { UIStateService } from '../../services/ui-state.service';
import { UserService } from '../../services/user.service';
import { AuthenticatedPageComponent } from '../base.component';
import { MainpageComponent } from '../mainpage/mainpage.component';
import { ComposeComponent } from './compose/compose.component';
import { ConversationComponent } from './conversation/conversation.component';

@Component({
    selector: 'app-mailbox',
    templateUrl: './mailbox.component.html',
    styleUrls: ['./mailbox.component.scss'],
    standalone: false,
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class MailboxComponent extends AuthenticatedPageComponent implements OnInit {

    public static route = 'mailbox';

    friendsSignal: Signal<Friendship[]>;
    // Set inside the friendRequests$ subscribe below (an external service subject), not a
    // template-bound handler - needs to be a signal for OnPush to see the update.
    protected inboundFriendRequests = signal<Friendship[]>(undefined);
    protected sentFriendRequests = signal<Friendship[]>(undefined);


    channelsSignal: Signal<Channel[]>;
    activeChannelsSignal: Signal<Channel[]>;
    composeOpen = false;

    alertsSignal: Signal<SystemNotification[]>;
    // Per-tab unread indicators - the envelope's own badge already sums everything together, but
    // that doesn't say which tab to open once you're here.
    conversationAlertsSignal: Signal<Notification[]>;
    buddyAlertsSignal: Signal<Notification[]>;

    constructor(public userService: UserService, private inboxService: InboxService, private chatService: ChatbarService,
        private systemNotificationService: SystemNotificationService, private randomEventService: RandomEventService,
        private notificationService: NotificationService,
        authService: AuthService, router: Router, uiStateService: UIStateService) {
        super(router, authService, MainpageComponent.route, uiStateService);
        this.channelsSignal = this.inboxService.allChannelsFlatSignal;
        this.activeChannelsSignal = this.chatService.activeChannelsSignal;
        this.friendsSignal = this.inboxService.friendsListSignal;
        this.alertsSignal = toSignal(this.systemNotificationService.pending$, { initialValue: [] });
        this.conversationAlertsSignal = this.notificationService.unreadPrivateMessagesSignal;
        this.buddyAlertsSignal = this.notificationService.unreadFriendRequestsSignal;

        this.subscribe(this.inboxService.friendRequests$, allReqs => {
            const user = this.activeUser$.getValue();
            this.sentFriendRequests.set((allReqs ?? []).filter(req => req.SenderUsername.localeCompare(user.Username) === 0));
            this.inboundFriendRequests.set((allReqs ?? []).filter(req => req.RecipientUsername.localeCompare(user.Username) === 0));
        });
    }

    ngOnInit(): void {
    }

    openChannel(channel: Channel): void {
        this.chatService.OpenChannel(channel);
        //this.chatService.UnreadMessages todo remove from unread messages
        //todo send read receipt
    }

    openChat(friendUsername: string) {
        const privateChat = this.inboxService.findPrivateChat(friendUsername);
        if (!privateChat) { //compose view
            const link = [`/${ComposeComponent.route}/${friendUsername}`];
            this.router.navigate(link);
        } else { //conversation view
            const link = [`/${ConversationComponent.route}/${privateChat.Id}`];
            this.router.navigate(link);
        }
    }

    acceptFriend(friendUsername: string) {
        this.subscriptions.push(this.inboxService.addFriend(friendUsername).subscribe());
    }

    removeFriend(friendUsername: string) {
        this.subscriptions.push(this.inboxService.removeFriend(friendUsername).subscribe());

    }

    // Opening an alert immediately acks it in all cases - see RandomEventService.openAndAck.
    // The row disappears from this list (and the envelope badge) as soon as it's clicked, not
    // when the popup is later closed. applyLocally is false - this notification's effect already
    // happened server-side in the past and SystemNotificationService already resynced cash/
    // inventory when it fetched this row, so patching the delta in again here would double it.
    openAlert(alert: SystemNotification): void {
        this.randomEventService.openAndAck(alert.Content, false);
    }
}
